/**
 * @file PayTrackerLanding.jsx
 * @description Standalone SEO landing page for the free "Pay & Hours" tracker, served at
 *   /pay-tracker. Built modular off TrackerShowcase so the homepage section and this page
 *   share the same faithful product mockups. Targets tracker-intent searches (RBT timesheet,
 *   1099 ABA expense tracker, BCBA invoicing, "what does my agency owe me") and funnels to
 *   signup — clicking the CTA stamps a "tracker" intent so new therapists land straight in
 *   Pay & Hours after onboarding. All copy is real crawlable DOM; FAQ emits FAQPage JSON-LD.
 *
 *   Positioning: "free for providers" — never "free forever" or "always free". We
 *   monetize agencies, not the people doing the work.
 * @module components/PayTrackerLanding
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  DollarIcon, CreditCardIcon, DownloadIcon, LockIcon, ShieldIcon,
  ClockIcon, CarIcon, BuildingIcon, FileTextIcon,
} from "./Icons";
import { HowItWorksStrip, IconTiles, RoleChips, MoreDetails, DetailList } from "./TrackerVisuals";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import StickyMobileCta from "./StickyMobileCta";
import TrackerShowcase from "./TrackerShowcase";
import { setSignupIntent } from "../lib/signupIntent";
import { track, A } from "../lib/analytics";
import { useFaqJsonLd, useSiteSchema, useBreadcrumbJsonLd } from "../lib/seoSchema";
import { usePageSeo } from "../lib/seo";

const D = {
  navy:      "#0F1024",
  navyMid:   "#1E2140",
  accent:    T.blue,
  accentGlow:"rgba(79,70,229,0.18)",
  border:    "rgba(226,232,240,0.8)",
  textMid:   "#475569",
  radius:    14,
};

// The long, keyword-rich feature write-ups. They now live collapsed under "More about the
// tracker" (still in the prerendered HTML for search); the page shows IconTiles instead.
const SECTIONS = [
  { titleKey: "payTrackerPage.sec1Title", bodyKey: "payTrackerPage.sec1Body" },
  { titleKey: "payTrackerPage.sec2Title", bodyKey: "payTrackerPage.sec2Body" },
  { titleKey: "payTrackerPage.sec3Title", bodyKey: "payTrackerPage.sec3Body" },
  { titleKey: "payTrackerPage.sec4Title", bodyKey: "payTrackerPage.sec4Body" },
  { titleKey: "payTrackerPage.sec5Title", bodyKey: "payTrackerPage.sec5Body" },
  { titleKey: "payTrackerPage.sec6Title", bodyKey: "payTrackerPage.sec6Body" },
];

// What the page SHOWS: icon + 2-3 word title + 3-4 word hint.
const TILES = [
  { Icon: ClockIcon,      key: "tile1" },
  { Icon: CreditCardIcon, key: "tile2" },
  { Icon: CarIcon,        key: "tile3" },
  { Icon: BuildingIcon,   key: "tile4" },
  { Icon: FileTextIcon,   key: "tile5" },
  { Icon: DownloadIcon,   key: "tile6" },
];

const TRUST = [
  { Icon: LockIcon,     key: "therapistLanding.trackerTrust1" },
  { Icon: ShieldIcon,   key: "therapistLanding.trackerTrust2" },
  { Icon: DownloadIcon, key: "therapistLanding.trackerTrust3" },
];

// Six dedicated FAQ entries scoped to this page (payTrackerPage namespace) — kept
// separate from therapistLanding.faq6/7/8, which TherapistLanding.jsx also reads.
const FAQ_ITEMS = [
  { qKey: "payTrackerPage.faqFreeQ", aKey: "payTrackerPage.faqFreeA" },
  { qKey: "payTrackerPage.faqRbtTimesheetQ", aKey: "payTrackerPage.faqRbtTimesheetA" },
  { qKey: "payTrackerPage.faqUnpaidQ", aKey: "payTrackerPage.faqUnpaidA" },
  { qKey: "payTrackerPage.faqMileageQ", aKey: "payTrackerPage.faqMileageA" },
  { qKey: "payTrackerPage.faqExportQ", aKey: "payTrackerPage.faqExportA" },
  { qKey: "payTrackerPage.faqPayrollQ", aKey: "payTrackerPage.faqPayrollA" },
];

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "#fff", border: `1.5px solid ${open ? D.accent + "33" : D.border}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", textAlign: "left", padding: "18px 22px", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700, color: D.navy, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontFamily: "'Inter','DM Sans',sans-serif" }}
      >
        {q}
        <span style={{ fontSize: 18, color: D.accent, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", display: "inline-block" }}>⌄</span>
      </button>
      {open && <div style={{ padding: "0 22px 18px", fontSize: 13, color: D.textMid, lineHeight: 1.75 }}>{a}</div>}
    </div>
  );
}

export default function PayTrackerLanding({ onSignUp }) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Own the full <head> for this standalone URL — title, description, canonical,
  // AND Open Graph / Twitter, so FB/Reddit unfurls show the TRACKER instead of
  // the generic marketplace OG from index.html. Prerender captures these.
  // ogTitle uses the cleaner hero line (the keyword metaTitle is for SEO/<title>).
  usePageSeo({
    title: t("payTrackerPage.metaTitle"),
    description: t("payTrackerPage.metaDescription"),
    canonical: "/free-aba-timesheet-payment-tracker",
    ogTitle: t("payTrackerPage.heroTitle"),
    ogDescription: t("payTrackerPage.metaDescription"),
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-paytracker", [
    { name: "Home", url: "/" },
    { name: "Free ABA Timesheet & Payment Tracker", url: "/free-aba-timesheet-payment-tracker" },
  ]);

  useFaqJsonLd("faq-jsonld-paytracker", FAQ_ITEMS.map(({ qKey, aKey }) => ({
    q: t(qKey),
    a: t(aKey),
  })));

  // Secondary CTA: case alerts (not "Browse ABA cases", which lands a guest on a
  // signup gate while Admin `public_case_browsing` is OFF).
  const getAlerts = () => { track(A.CASE_ALERT_CLICK, { supabaseMeta: { page: "/free-aba-timesheet-payment-tracker/" } }); setSignupIntent("alerts"); onSignUp?.(); };
  const startTracking = () => { track(A.TRACKER_SIGNUP_STARTED, { supabaseMeta: { source: "pay_tracker_landing" } }); setSignupIntent("tracker"); onSignUp?.(); };
  const sectionPad = isMobile ? "52px 16px" : "72px 48px";

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#F8FAFC", fontFamily: "'Inter','DM Sans',sans-serif" }}>
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div style={{ background: `linear-gradient(135deg, ${D.navy} 0%, ${D.navyMid} 60%, #1a1f4a 100%)`, padding: isMobile ? "56px 16px 64px" : "76px 48px 92px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, left: "20%", width: 400, height: 400, borderRadius: "50%", background: "rgba(79,70,229,0.18)", filter: "blur(80px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a5b4fc", fontFamily: "'JetBrains Mono', monospace" }}>
            {t("payTrackerPage.eyebrow", "Built into ABAcases")}
          </span>
          <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 900, color: "#fff", lineHeight: 1.14, letterSpacing: "-0.03em", margin: "14px 0 18px" }}>
            {t("payTrackerPage.heroTitle", "Free ABA Timesheet & Payment Tracker")}
          </h1>
          <p style={{ fontSize: 16.5, color: "rgba(255,255,255,0.64)", lineHeight: 1.7, maxWidth: 620, margin: "0 auto 36px" }}>
            {t("payTrackerPage.heroSubtitle", "See when each agency should pay you, and get flagged when a payment is late.")}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={startTracking} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", borderRadius: 10, background: T.gradientIndigo, color: "#fff", border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 20px rgba(79,70,229,0.4)", fontFamily: "'Inter',sans-serif" }}>
              <DollarIcon size={16} color="#fff" />
              {t("payTrackerPage.heroCta", "Start tracking")}
            </button>
            <button onClick={getAlerts} style={{ padding: "14px 28px", borderRadius: 10, background: "rgba(255,255,255,0.07)", border: "1.5px solid rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.85)", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              {t("payTrackerPage.ctaAlerts", "Get case alerts")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Live showcase (dark band, reuses homepage frames) ─────── */}
      <div style={{ background: `linear-gradient(180deg, ${D.navy} 0%, #1a1f4a 100%)`, padding: isMobile ? "8px 12px 60px" : "16px 48px 80px" }}>
        {/* On phones the calendar frame is too cramped, so the showcase skips it (same as home). */}
        <TrackerShowcase isMobile={isMobile} accent={D.accent} mobileHideTabs={["calendar"]} />
        <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 10 : 14, flexWrap: "wrap", maxWidth: 920, margin: "32px auto 0" }}>
          {TRUST.map((tp) => (
            <div key={tp.key} style={{ display: "flex", alignItems: "center", gap: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 100, padding: "9px 16px" }}>
              <tp.Icon size={15} color="#a5b4fc" />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.82)" }}>{t(tp.key)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works: three steps, the third is why you sign up ─ */}
      <div style={{ padding: isMobile ? "44px 16px 8px" : "64px 48px 12px" }}>
        <HowItWorksStrip isMobile={isMobile} />
      </div>

      {/* ── What's inside: shown as tiles, the long write-ups collapsed ─ */}
      <div style={{ padding: isMobile ? "36px 16px 52px" : "48px 48px 72px", maxWidth: 1000, margin: "0 auto" }}>
        <h2 style={{ textAlign: "center", fontSize: isMobile ? 22 : 28, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: "0 0 20px" }}>
          {t("payTrackerPage.insideTitle", "Everything your ABA timesheet and payment tracker covers")}
        </h2>
        <IconTiles isMobile={isMobile} items={TILES.map((x) => ({ Icon: x.Icon, title: t(`trackerVisual.${x.key}Title`), hint: t(`trackerVisual.${x.key}Hint`) }))} />
        <div style={{ marginTop: 18 }}><RoleChips /></div>
        <p style={{ textAlign: "center", fontSize: 14, color: D.textMid, margin: "18px 0 0" }}>
          {t("trackerVisual.alertsLine")}{" · "}
          <button onClick={getAlerts} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.indigo, fontSize: 14, fontWeight: 700, textDecoration: "underline", fontFamily: "inherit" }}>
            {t("trackerVisual.alertsLink")}
          </button>
        </p>
        <MoreDetails summary={t("trackerVisual.moreTracker")}>
          <p style={{ fontSize: 14, color: D.textMid, lineHeight: 1.7, margin: "0 0 14px" }}>{t("payTrackerPage.insideSubtitle")}</p>
          <DetailList items={[...SECTIONS, { titleKey: "payTrackerPage.sec7Title", bodyKey: "payTrackerPage.sec7Body" }].map((x) => ({ title: t(x.titleKey), body: t(x.bodyKey) }))} />
        </MoreDetails>
      </div>

      {/* ── FAQ (FAQPage JSON-LD emitted via hook) ────────────────── */}
      <div style={{ background: "#fff", padding: sectionPad, borderTop: `1px solid ${D.border}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ fontSize: isMobile ? 26 : 33, fontWeight: 800, color: D.navy, letterSpacing: "-0.02em", margin: 0 }}>
              {t("payTrackerPage.faqTitle", "Questions about the tracker")}
            </h2>
          </div>
          {FAQ_ITEMS.map(({ qKey, aKey }, i) => (
            <FaqItem key={qKey} q={t(qKey)} a={t(aKey)} defaultOpen={i === 0} />
          ))}
        </div>
      </div>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
      <StickyMobileCta label={t("stickyCta.startTracking")} onClick={startTracking} ctaId="tracker" />
    </div>
  );
}
